import React, { FC, useCallback, useEffect, useMemo } from "react";
import { generatePath, useNavigate, useParams } from "react-router";
import { Layout } from "@lookiero/sty-psp-ui";
import { RafComponent } from "../../Root";
import { useViewOrderById } from "../../behaviors/useViewOrderById";
import { CheckoutConfirmation as CheckoutConfirmationTemplate } from "../../components/templates/checkoutConfirmation/CheckoutConfirmation";
import { useStaticInfo } from "../../hooks/useStaticInfo";
import { Routes } from "../../routing/routes";
import { TrackingPage, TrackingView } from "../../tracking/tracking";
import { useTrackCheckoutSuccess } from "../../tracking/useTrackCheckoutSuccess";
import { useTrackLookAndLikeBannerClicked } from "../../tracking/useTrackLookAndLikeBannerClicked";
import { useTrackPageView } from "../../tracking/useTrackPageView";
import { useTrackPaymentResult } from "../../tracking/useTrackPaymentResult";
import { placeNewOrder } from "./CheckoutConfirmation.controller";

interface CheckoutConfirmationProps {
  readonly layout: Layout;
  readonly rafComponent: RafComponent;
  readonly onPressLookAndLikeBanner: () => void;
}

const CheckoutConfirmation: FC<CheckoutConfirmationProps> = ({ layout, rafComponent, onPressLookAndLikeBanner }) => {
  const {
    session: { auth, user },
    basePath,
    setIsPaymentOK,
    tradename,
    apiUrl,
  } = useStaticInfo();
  const { orderId: orderIdFromRoute } = useParams();
  const navigate = useNavigate();

  const trackingCommonParams = useMemo(
    () => ({
      country: user.country,
      segment: user.segment,
      userId: user.id,
      tradename,
    }),
    [tradename, user.country, user.id, user.segment],
  );
  useTrackPageView({
    ...trackingCommonParams,
    page: TrackingPage.CHECKOUT_CONFIRMATION,
    view: TrackingView.LOOK_IN_CHECKOUT_SUCCESS,
  });
  const trackCheckoutSuccess = useTrackCheckoutSuccess(trackingCommonParams);
  const trackPaymentResult = useTrackPaymentResult(trackingCommonParams);
  const trackLookAndLikeBannerClicked = useTrackLookAndLikeBannerClicked(trackingCommonParams);

  const order = useViewOrderById(orderIdFromRoute as string);

  const handleOnClose = useCallback(() => {
    setIsPaymentOK(false);
    navigate(`${basePath}/${Routes.HOME}`);
  }, [basePath, navigate, setIsPaymentOK]);

  const handleOnPressGoToOrder = useCallback(() => {
    if (!order) {
      return;
    }

    navigate(generatePath(`${basePath}/${Routes.ORDER}`, { orderId: order.orderId }));
  }, [basePath, navigate, order]);

  useEffect(() => {
    placeNewOrder(auth, apiUrl);
  }, [apiUrl, auth]);

  useEffect(() => {
    if (order) {
      setIsPaymentOK(true);
      trackCheckoutSuccess({ order });
      trackPaymentResult({ orderId: order.orderId, success: true });
    }
  }, [order, setIsPaymentOK, trackCheckoutSuccess, trackPaymentResult, user]);

  const handleOnPressBanner = useCallback(() => {
    trackLookAndLikeBannerClicked();

    onPressLookAndLikeBanner();
  }, [onPressLookAndLikeBanner, trackLookAndLikeBannerClicked]);

  return order ? (
    <CheckoutConfirmationTemplate
      layout={layout}
      orderReference={order.reference}
      rafComponent={rafComponent}
      onClose={handleOnClose}
      onPressBanner={handleOnPressBanner}
      onPressGoToOrder={handleOnPressGoToOrder}
      onPressReturnToShop={handleOnClose}
    />
  ) : null;
};

export { CheckoutConfirmation };
